import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Tooltip,
  VisuallyHidden,
  useBoolean,
} from '@chakra-ui/react';
import { Code } from 'constants/keyboard';
import { COLOR_EDITED } from 'constants/mixpanel';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { FocusEvent, ReactElement, useCallback, useId, useRef, useState } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';
import { getAlpha, getColorWithAlphaString, removeHexAlpha, toHex } from 'utils/color';

import { BrandColor } from './BrandColor';
import { ColorDisplay } from './ColorDisplay/ColorDisplay';
import { ColorInput } from './ColorInput/ColorInput';
import {
  CLOSE_ICON_EL_ID,
  COLOR_PICKER_POPOVER_WIDTH,
  HEX_NO_COLOR,
  NO_COLOR,
  TRIGGER_BUTTON_SIZE,
} from './ColorPicker.config';
import { ColorPickerCustomStyle, StepColorPickerStyle } from './ColorPicker.style';
import { ColorPickerContentContainerProps, ColorPickerProps } from './ColorPicker.types';
import { ColorPickerDivider } from './ColorPickerDivider';
import { ColorSwatch } from './ColorSwatch';
import { DefaultTrigger } from './DefaultTrigger';
import { EyeDropper } from './EyeDropper';
import { HexAlphaColorPickerWrapper } from './HexAlphaColorPickerWrapper';
import { OpacityInput } from './OpacityInput/OpacityInput';

const panelTestId = 'colorpicker-panel';
const popoverContentTestId = 'colorpicker-popover-content';
const NO_COLOR_OPTIONS = [HEX_NO_COLOR, NO_COLOR];

export const ColorPicker = ({
  children,
  brandColors,
  brandSwitchMenu,
  brandUpgrade,
  saturationBorderColor,
  hasBrandPermission = false,
  color,
  showBrandSection = true,
  onChange,
  onContainerBlur,
  label,
  showNoColorOption = true,
  iconStyle = 'fill',
  placement = 'bottom-start',
  colorSwatch = [],
  onSetSwatchColor,
  onRemoveSwatchColor,
  hasArrow = false,
  showOpacityOption = true,
  onOpen,
  onClose,
  iconColors = [],
  h: hProp,
  w: wProp,
  brandComponent,
  skipOutsideOnCloseIds = [],
}: ColorPickerProps): ReactElement => {
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  const containerId = useId();

  const colorRef = useRef(color);
  colorRef.current = color;
  const addButtonRef = useRef<HTMLButtonElement>(null);

  const [isTooltipOpen, setIsTooltipOpen] = useBoolean(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const isStep = iconStyle === 'step';
  const h = hProp ?? isStep ? 'full' : hProp;
  const w = wProp ?? isStep ? 'full' : wProp;

  const onSetPopover = useCallback(
    (isOpen: boolean) => {
      if (isOpen === isPopoverOpen) return;

      setIsPopoverOpen(isOpen);
      if (isOpen) onOpen?.();
      else onClose?.();
    },
    [onClose, onOpen, isPopoverOpen],
  );

  const hexString = toHex(color) !== color ? toHex(color) : color;

  const setSwatchColor = () => onSetSwatchColor(color);

  const mpDebouncedTrack = useDebouncedCallback((mpFromProp: string) => {
    Mixpanel.track(COLOR_EDITED.eventName, {
      from: mpFromProp,
    });
  }, 500);

  const onChangeColor = useCallback(
    (mpFromProp?: string) => (newColor: string) => {
      if (mpFromProp) mpDebouncedTrack(mpFromProp);
      // if the selected color is "no-color" remove the alpha transparency for the new color
      if (NO_COLOR_OPTIONS.includes(colorRef.current)) return onChange(removeHexAlpha(newColor));
      return onChange(toHex(newColor));
    },
    [onChange, mpDebouncedTrack],
  );

  const onChangeOpacity = useCallback(
    (newOpacity: number) => {
      onChange(toHex(getColorWithAlphaString(colorRef.current, newOpacity / 100)));
    },
    [onChange],
  );

  const closePopover = useCallback(() => {
    setIsTooltipOpen.off();
    onSetPopover(false);
  }, [onSetPopover, setIsTooltipOpen]);

  const StyledContainer = isStep ? StepColorPickerStyle : ColorPickerCustomStyle;

  const onKeyDown = (e: React.KeyboardEvent) => {
    // close popover on escape - focus on the trigger button
    if (e.key === Code.Escape && isPopoverOpen) {
      e.stopPropagation();
      closePopover();
      if (triggerButtonRef.current) triggerButtonRef.current.focus();
    }
  };

  const onClickTrigger = () => {
    if (!isPopoverOpen) onSetPopover(true);
  };

  const onStyledContainerBlur = (e: FocusEvent) => {
    if (!onContainerBlur) return;
    onContainerBlur(e, isPopoverOpen);
  };

  return (
    <StyledContainer onBlur={onStyledContainerBlur} id={containerId} h={h} w={w}>
      <Tooltip hasArrow isOpen={isTooltipOpen && !isPopoverOpen} placement='bottom' label={label}>
        <Box
          h={h}
          w={w}
          maxH={TRIGGER_BUTTON_SIZE}
          onMouseEnter={setIsTooltipOpen.on}
          onMouseLeave={setIsTooltipOpen.off}
        >
          <Popover isOpen={isPopoverOpen} placement={placement} onClose={closePopover} closeOnBlur={false} isLazy>
            <PopoverTrigger>
              <Flex h={h} w={w}>
                <DefaultTrigger
                  iconStyle={iconStyle}
                  color={hexString}
                  iconColors={iconColors}
                  triggerButtonRef={triggerButtonRef}
                  onBlur={setIsTooltipOpen.off}
                  onFocus={setIsTooltipOpen.on}
                  onClick={onClickTrigger}
                  label={label}
                />
              </Flex>
            </PopoverTrigger>
            <PopoverContent
              onKeyDown={onKeyDown}
              w='fit-content'
              minW={`${COLOR_PICKER_POPOVER_WIDTH}px`}
              boxShadow='md'
              padding={0}
              data-testid={popoverContentTestId}
            >
              {hasArrow && <PopoverArrow />}
              <PopoverCloseButton id={CLOSE_ICON_EL_ID} />
              <VisuallyHidden>
                <PopoverHeader>{label}</PopoverHeader>
              </VisuallyHidden>
              <ColorPickerContentContainer
                closePopover={closePopover}
                containerId={[containerId, ...skipOutsideOnCloseIds]}
              >
                <Box m={4}>
                  <Box as='fieldset'>
                    <ColorSwatch
                      onSetSwatchColor={setSwatchColor}
                      onRemoveSwatchColor={onRemoveSwatchColor}
                      colorSwatch={colorSwatch}
                      onChange={onChangeColor(COLOR_EDITED.from.Swatch)}
                      color={color}
                      showNoColorOption={showNoColorOption}
                      addButtonRef={addButtonRef}
                    />
                  </Box>
                  {showBrandSection && (
                    <BrandColor
                      brandColors={brandColors ?? []}
                      brandSwitchMenu={brandSwitchMenu}
                      brandUpgrade={brandUpgrade}
                      color={color}
                      hasBrandPermission={hasBrandPermission}
                      onChange={onChangeColor(COLOR_EDITED.from.BrandkitColor)}
                      brandComponent={brandComponent}
                    />
                  )}
                  <ColorPickerDivider />
                  <Flex justifyContent='space-between' mb={2} mt={4}>
                    <ColorInput color={color} onChange={onChange} isPopoverOpen={isPopoverOpen} />
                    {showOpacityOption && <OpacityInput opacity={getAlpha(color)} onChange={onChangeOpacity} />}
                  </Flex>
                  <Flex>
                    <Flex direction='column' gap='8px' justifyContent='space-between'>
                      <Box w={6} h={6}>
                        <EyeDropper onChange={onChangeColor(COLOR_EDITED.from.EyeDropper)} />
                      </Box>
                      <ColorDisplay color={color} borderColor={saturationBorderColor} />
                    </Flex>
                    <HexAlphaColorPickerWrapper
                      showOpacityOption={showOpacityOption}
                      saturationBorderColor={saturationBorderColor}
                    >
                      <HexAlphaColorPicker color={hexString} onChange={onChangeColor(COLOR_EDITED.from.ColorPicker)} />
                    </HexAlphaColorPickerWrapper>
                  </Flex>
                </Box>
                <Box>{children}</Box>
              </ColorPickerContentContainer>
            </PopoverContent>
          </Popover>
        </Box>
      </Tooltip>
    </StyledContainer>
  );
};

const ColorPickerContentContainer = ({ children, closePopover, containerId }: ColorPickerContentContainerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({ ref: containerRef, handler: closePopover, ignoreId: containerId });

  return (
    <Box ref={containerRef} w={`${COLOR_PICKER_POPOVER_WIDTH}px`} data-testid={panelTestId}>
      {children}
    </Box>
  );
};
